import { Link } from "react-router-dom";
import { ShieldCheck, ArrowLeft } from "lucide-react";

/**
 * Public LGPD-compliant Privacy Policy.
 * Linked from Google Play Console listing and from the login footer.
 */
const PrivacyPolicy = () => {
  const sections = [
    {
      title: "1. Quem somos",
      body: (
        <>
          <p>
            <strong>AVI Serviços de Segurança Ltda.</strong> (denominada
            simplesmente <em>"AVI Segurança"</em>) — CNPJ a ser identificado em
            contrato — sediada em Salvador/BA, é a controladora dos dados
            tratados neste aplicativo "<strong>AVI Segurança — Portal do
            Funcionário</strong>".
          </p>
          <p>
            Esta Política descreve, em linguagem clara, como coletamos, usamos,
            armazenamos e protegemos as informações pessoais dos colaboradores
            que utilizam o aplicativo.
          </p>
        </>
      ),
    },
    {
      title: "2. Dados que coletamos",
      body: (
        <ul className="list-disc pl-5 space-y-1">
          <li>
            <strong>Identificação funcional</strong>: matrícula, nome completo,
            CPF e dados de contato (telefone e e-mail) fornecidos pela própria
            empresa empregadora.
          </li>
          <li>
            <strong>Documentos trabalhistas</strong>: contracheques, informes
            de rendimentos, avisos e recibos de férias, kit de admissão e
            listas de presença em treinamentos.
          </li>
          <li>
            <strong>Assinatura eletrônica</strong>: rúbrica desenhada na tela e
            metadados da assinatura (IP, dispositivo, navegador, data e hora,
            hash SHA-256 do documento).
          </li>
          <li>
            <strong>Acessos</strong>: data e hora de cada login, para fins de
            auditoria.
          </li>
        </ul>
      ),
    },
    {
      title: "3. Como usamos seus dados",
      body: (
        <ul className="list-disc pl-5 space-y-1">
          <li>
            Permitir que você consulte e baixe documentos trabalhistas a
            qualquer momento.
          </li>
          <li>
            Coletar assinaturas eletrônicas com validade jurídica, conforme a
            MP 2.200-2/2001 e a Lei 14.063/2020.
          </li>
          <li>
            Enviar códigos de verificação (OTP) por SMS ou e-mail para
            confirmar sua identidade ao assinar.
          </li>
          <li>
            Cumprir obrigações legais trabalhistas, previdenciárias e fiscais.
          </li>
        </ul>
      ),
    },
    {
      title: "4. Compartilhamento",
      body: (
        <p>
          Seus dados <strong>NÃO são vendidos nem compartilhados</strong> com
          terceiros para fins comerciais. Compartilhamos apenas com prestadores
          técnicos estritamente necessários ao funcionamento do app (provedor
          de SMS — Twilio; provedor de e-mail — KingHost; provedor de banco de
          dados — MongoDB Atlas), todos sob obrigação contratual de
          confidencialidade.
        </p>
      ),
    },
    {
      title: "5. Segurança",
      body: (
        <ul className="list-disc pl-5 space-y-1">
          <li>Comunicação criptografada (HTTPS/TLS) em todas as conexões.</li>
          <li>Senhas e códigos OTP armazenados como hash, nunca em texto puro.</li>
          <li>
            Acesso restrito por matrícula + CPF e por código de verificação
            de dois fatores (OTP).
          </li>
          <li>
            Cada documento assinado recebe um hash SHA-256 e um QR Code
            público para validação independente.
          </li>
        </ul>
      ),
    },
    {
      title: "6. Seus direitos (LGPD — Lei 13.709/2018)",
      body: (
        <ul className="list-disc pl-5 space-y-1">
          <li>Confirmar a existência de tratamento dos seus dados.</li>
          <li>Acessar, corrigir, anonimizar ou solicitar a portabilidade.</li>
          <li>
            Solicitar a eliminação dos dados (respeitando obrigações legais de
            guarda trabalhista, geralmente 30 anos).
          </li>
          <li>Revogar o consentimento a qualquer momento.</li>
        </ul>
      ),
    },
    {
      title: "7. Retenção",
      body: (
        <p>
          Mantemos os dados pelo tempo exigido pela legislação trabalhista e
          previdenciária brasileira (em regra, até 30 anos após o desligamento
          para FGTS; demais documentos seguem prazos específicos da CLT, INSS
          e Receita Federal).
        </p>
      ),
    },
    {
      title: "8. Contato do Encarregado (DPO)",
      body: (
        <p>
          Para exercer seus direitos ou esclarecer dúvidas sobre privacidade,
          escreva para: <a
            href="mailto:segurancadotrabalho@grupoavi.com.br"
            className="text-blue-600 underline"
          >segurancadotrabalho@grupoavi.com.br</a>.
        </p>
      ),
    },
    {
      title: "9. Alterações",
      body: (
        <p>
          Esta política pode ser atualizada. A versão vigente sempre estará
          disponível em <strong>portal.grupoavi.com.br/privacidade</strong>.
          Última atualização: <strong>18 de maio de 2026</strong>.
        </p>
      ),
    },
  ];

  return (
    <div className="min-h-screen bg-slate-50" data-testid="privacy-policy-page">
      <header className="bg-slate-900 text-white">
        <div className="max-w-3xl mx-auto px-5 py-6 flex items-center gap-3">
          <ShieldCheck className="w-7 h-7 text-emerald-400" />
          <div>
            <h1 className="text-xl font-semibold">Política de Privacidade</h1>
            <p className="text-xs text-slate-300">
              AVI Segurança — Portal do Funcionário
            </p>
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-5 py-8 space-y-7 text-slate-700 text-sm leading-relaxed">
        <Link
          to="/"
          className="inline-flex items-center gap-1 text-blue-600 hover:underline text-xs"
          data-testid="privacy-back-link"
        >
          <ArrowLeft className="w-3 h-3" /> Voltar ao login
        </Link>

        <p className="text-slate-600">
          A privacidade dos nossos colaboradores é prioridade. Este documento
          explica de forma transparente como tratamos seus dados pessoais no
          aplicativo.
        </p>

        {sections.map((s) => (
          <section key={s.title} className="space-y-2">
            <h2 className="text-base font-semibold text-slate-900">{s.title}</h2>
            <div className="text-[13px] space-y-2">{s.body}</div>
          </section>
        ))}

        <footer className="pt-6 border-t border-slate-200 text-center text-xs text-slate-400">
          © {new Date().getFullYear()} AVI Serviços de Segurança Ltda.
        </footer>
      </main>
    </div>
  );
};

export default PrivacyPolicy;
